import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, ShoppingBag, Package, Euro, Upload } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";

interface Product {
  id: string;
  title: string;
  short_description: string;
  full_description: string;
  category: string;
  price: number;
  benefits: string | null;
  composition: string | null;
  usage: string | null;
  precautions: string | null;
  image_url: string | null;
  stock_quantity: number | null;
  is_active: boolean;
  display_order: number;
}

const PRODUCT_CATEGORIES = [
  "Tisanes & Infusions",
  "Roll-on Bien-Être",
  "Baumes & Produits Artisanaux",
  "Produits Numériques (PDF)",
  "Kits Bien-Être",
];

export default function ProductsManagement() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const { toast } = useToast();
  const [imageSource, setImageSource] = useState<"url" | "upload">("url");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    short_description: "",
    full_description: "",
    category: "Tisanes & Infusions",
    price: 0,
    benefits: "",
    composition: "",
    usage: "",
    precautions: "",
    image_url: "",
    stock_quantity: 0,
    is_active: false,
    display_order: 0,
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("user_id", user.id)
        .order("display_order", { ascending: true });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les produits",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const productData = {
        ...formData,
        user_id: user.id,
        benefits: formData.benefits || null,
        composition: formData.composition || null,
        usage: formData.usage || null,
        precautions: formData.precautions || null,
        stock_quantity: formData.stock_quantity || null,
      };

      if (editingProduct) {
        const { error } = await supabase
          .from("products")
          .update(productData)
          .eq("id", editingProduct.id);

        if (error) throw error;
        toast({ title: "Produit modifié avec succès" });
      } else {
        const { error } = await supabase
          .from("products")
          .insert([productData]);

        if (error) throw error;
        toast({ title: "Produit créé avec succès" });
      }

      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error("Error saving product:", error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder le produit",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      short_description: product.short_description,
      full_description: product.full_description,
      category: product.category,
      price: product.price,
      benefits: product.benefits || "",
      composition: product.composition || "",
      usage: product.usage || "",
      precautions: product.precautions || "",
      image_url: product.image_url || "",
      stock_quantity: product.stock_quantity || 0,
      is_active: product.is_active,
      display_order: product.display_order,
    });
    setImageSource(
      product.image_url?.includes("supabase.co/storage") ? "upload" : "url"
    );
    setDialogOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const ext = file.name.split(".").pop();
      const fileName = `products/${user.id}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("assets")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("assets")
        .getPublicUrl(fileName);

      setFormData((prev) => ({ ...prev, image_url: publicUrl }));
      toast({ title: "Image uploadée avec succès" });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({ title: "Erreur", description: "Impossible d'uploader l'image", variant: "destructive" });
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) return;

    try {
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Produit supprimé avec succès" });
      fetchProducts();
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le produit",
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (product: Product) => {
    try {
      const { error } = await supabase
        .from("products")
        .update({ is_active: !product.is_active })
        .eq("id", product.id);

      if (error) throw error;
      toast({
        title: product.is_active ? "Produit désactivé" : "Produit activé",
      });
      fetchProducts();
    } catch (error) {
      console.error("Error toggling product:", error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le produit",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      short_description: "",
      full_description: "",
      category: "Tisanes & Infusions",
      price: 0,
      benefits: "",
      composition: "",
      usage: "",
      precautions: "",
      image_url: "",
      stock_quantity: 0,
      is_active: false,
      display_order: 0,
    });
    setEditingProduct(null);
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-secondary/10 flex items-center justify-center animate-pulse">
            <ShoppingBag className="w-6 h-6 text-secondary" />
          </div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Boutique Bien-être"
        subtitle="Gérez vos produits à afficher sur votre site vitrine"
        icon={<ShoppingBag className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="secondary"
        actions={
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-gradient-primary">
                <Plus className="h-4 w-4" />
                Nouveau produit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {editingProduct ? "Modifier le produit" : "Créer un nouveau produit"}
                </DialogTitle>
              </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Titre *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Catégorie *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="short_description">Description courte *</Label>
                    <Textarea
                      id="short_description"
                      value={formData.short_description}
                      onChange={(e) =>
                        setFormData({ ...formData, short_description: e.target.value })
                      }
                      rows={2}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="full_description">Description complète *</Label>
                    <Textarea
                      id="full_description"
                      value={formData.full_description}
                      onChange={(e) =>
                        setFormData({ ...formData, full_description: e.target.value })
                      }
                      rows={4}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="price">Prix (€) *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: parseFloat(e.target.value) })
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="benefits">Bénéfices</Label>
                    <Textarea
                      id="benefits"
                      value={formData.benefits}
                      onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                      rows={3}
                      placeholder="Détaillez les bienfaits du produit..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="composition">Composition</Label>
                    <Textarea
                      id="composition"
                      value={formData.composition}
                      onChange={(e) => setFormData({ ...formData, composition: e.target.value })}
                      rows={3}
                      placeholder="Listez les ingrédients et la composition..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="usage">Utilisation</Label>
                    <Textarea
                      id="usage"
                      value={formData.usage}
                      onChange={(e) => setFormData({ ...formData, usage: e.target.value })}
                      rows={3}
                      placeholder="Expliquez comment utiliser le produit..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="precautions">Précautions</Label>
                    <Textarea
                      id="precautions"
                      value={formData.precautions}
                      onChange={(e) => setFormData({ ...formData, precautions: e.target.value })}
                      rows={2}
                      placeholder="Précautions d'emploi, contre-indications..."
                    />
                  </div>

                  <div className="space-y-3">
                    <Label>Image du produit</Label>
                    <RadioGroup
                      value={imageSource}
                      onValueChange={(v) => setImageSource(v as "url" | "upload")}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="url" id="img-url" />
                        <Label htmlFor="img-url" className="cursor-pointer">URL</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="upload" id="img-upload" />
                        <Label htmlFor="img-upload" className="cursor-pointer">Uploader</Label>
                      </div>
                    </RadioGroup>

                    {imageSource === "url" ? (
                      <Input
                        id="image_url"
                        value={formData.image_url}
                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                        placeholder="https://..."
                      />
                    ) : (
                      <div className="space-y-2">
                        <input
                          type="file"
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="gap-2"
                        >
                          <Upload className="h-4 w-4" />
                          {uploadingImage ? "Upload en cours..." : "Choisir une image"}
                        </Button>
                      </div>
                    )}

                    {formData.image_url && (
                      <div className="mt-2">
                        <img
                          src={formData.image_url}
                          alt="Aperçu"
                          className="max-h-32 rounded-md border object-contain"
                        />
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="stock_quantity">Stock disponible</Label>
                      <Input
                        id="stock_quantity"
                        type="number"
                        value={formData.stock_quantity}
                        onChange={(e) =>
                          setFormData({ ...formData, stock_quantity: parseInt(e.target.value) })
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="display_order">Ordre d'affichage</Label>
                      <Input
                        id="display_order"
                        type="number"
                        value={formData.display_order}
                        onChange={(e) =>
                          setFormData({ ...formData, display_order: parseInt(e.target.value) })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_active: checked })
                      }
                    />
                    <Label htmlFor="is_active">Activer sur le site vitrine</Label>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                    >
                      Annuler
                    </Button>
                    <Button type="submit">
                      {editingProduct ? "Modifier" : "Créer"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <Card key={product.id} className={!product.is_active ? "opacity-60" : ""}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{product.title}</CardTitle>
                    <CardDescription className="mt-1">
                      {product.category}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(product)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(product.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {product.short_description}
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-secondary">
                    {product.price}€
                  </span>
                  {product.stock_quantity !== null && (
                    <span className="text-sm text-muted-foreground">
                      Stock: {product.stock_quantity}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm">
                    {product.is_active ? "Visible sur le site" : "Non visible"}
                  </span>
                  <Switch
                    checked={product.is_active}
                    onCheckedChange={() => toggleActive(product)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {products.length === 0 && (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-muted-foreground">
                Aucun produit créé. Créez votre premier produit pour commencer.
              </p>
            </CardContent>
          </Card>
        )}
    </PageContainer>
  );
}
